import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useClasses, useDepartments, useSubclasses } from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  ANCHOR_DETAIL_TOTAL_DAYS_DIFF,
  buildPogDetailRows,
  buildProjectionTableData,
  DC_OPTIONS,
  EVENT_OPTIONS,
  INITIAL_REPLEN_OPTIONS,
  isDefaultDetailFilterState,
  PO_OPTIONS,
  POG_GROUP_OPTIONS,
  POG_SET_TYPE_OPTIONS,
  SEASONAL_CORE_OPTIONS,
  SKU_OPTIONS,
  SNAPSHOT_DATE_OPTIONS,
  VENDOR_OPTIONS,
  type PogFilterState,
} from './pogOnTimeProjectionMockData'
import { POGOnTimeProjectionTable } from './POGOnTimeProjectionTable'
import { POGDetailsTable } from './POGDetailsTable'

const ALL = '__all__'

type ActiveTab = 'pog-on-time-projection' | 'pog-details'

const DESCRIPTIONS: Record<ActiveTab, string> = {
  'pog-on-time-projection':
    'Summarizes POG readiness by department and POG, combining sales importance with projected % on-time coverage over the next few weeks. The on-time percentage is based on the total number of SKUs with open orders, while Sellable Units represent the projected open-order units expected to become available for sale.',
  'pog-details':
    'Line-level detail of PO/SKU/vendor records showing in-store date versus POG set date and the resulting day differential for the selected snapshot.',
}

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface IdOption {
  id: string
  label: string
}

function IdSelect({
  value,
  onValueChange,
  options,
  loading,
  disabled,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: IdOption[]
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.id} value={o.id}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export function POGOnTimeProjectionMockup() {
  const [activeTab, setActiveTab] = useState<ActiveTab>(
    'pog-on-time-projection'
  )

  const [snapshotDate, setSnapshotDate] = useState<string>('07/01/2026')
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)
  const [dc, setDc] = useState<string | null>(null)
  const [pogSetDateStart, setPogSetDateStart] = useState<string | null>(null)
  const [pogSetDateEnd, setPogSetDateEnd] = useState<string | null>(null)
  const [seasonalCore, setSeasonalCore] = useState<string | null>(null)
  const [seasonId, setSeasonId] = useState<string | null>(null)
  const [event, setEvent] = useState<string | null>(null)
  const [pogGroup, setPogGroup] = useState<string | null>(null)
  const [pogSetType, setPogSetType] = useState<string | null>(null)
  const [initialReplen, setInitialReplen] = useState<string | null>(null)
  // Details tab only, but hoisted so they persist across tab switches.
  const [po, setPo] = useState<string | null>(null)
  const [sku, setSku] = useState<string | null>(null)
  const [vendor, setVendor] = useState<string | null>(null)

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)
  const { data: seasons, isLoading: seasonsLoading } = useSeasons()

  const departmentOptions: IdOption[] = (departments ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const classOptions: IdOption[] = (classes ?? []).map(c => ({
    id: c.id,
    label: `${c.code} - ${c.name}`,
  }))
  const subclassOptions: IdOption[] = (subclasses ?? []).map(s => ({
    id: s.id,
    label: `${s.code} - ${s.name}`,
  }))
  const seasonOptions: IdOption[] = (seasons ?? []).map(s => ({
    id: s.id,
    label: s.name,
  }))

  const filters = useMemo<PogFilterState>(
    () => ({
      snapshotDate,
      departmentId,
      classId,
      subclassId,
      dc,
      pogSetDateStart,
      pogSetDateEnd,
      seasonalCore,
      seasonId,
      event,
      pogGroup,
      pogSetType,
      initialReplen,
      po,
      sku,
      vendor,
    }),
    [
      snapshotDate,
      departmentId,
      classId,
      subclassId,
      dc,
      pogSetDateStart,
      pogSetDateEnd,
      seasonalCore,
      seasonId,
      event,
      pogGroup,
      pogSetType,
      initialReplen,
      po,
      sku,
      vendor,
    ]
  )

  const projectionData = useMemo(
    () => buildProjectionTableData(filters),
    [filters]
  )
  const detailRows = useMemo(() => buildPogDetailRows(filters), [filters])
  const detailsTotalOverride = useMemo(
    () =>
      isDefaultDetailFilterState(filters)
        ? ANCHOR_DETAIL_TOTAL_DAYS_DIFF
        : undefined,
    [filters]
  )

  const handleDepartmentChange = (v: string | null) => {
    setDepartmentId(v)
    setClassId(null)
    setSubclassId(null)
  }

  const handleClassChange = (v: string | null) => {
    setClassId(v)
    setSubclassId(null)
  }

  const isDetailsTab = activeTab === 'pog-details'

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Top strip */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="max-w-2xl text-[11px] leading-tight text-muted-foreground">
            {DESCRIPTIONS[activeTab]}
          </p>
          {!isDetailsTab && (
            <div className="mt-2 max-w-2xl text-[11px] leading-tight text-muted-foreground">
              <p className="font-medium">
                Color Status Rules ("By set" column)
              </p>
              <p>
                <span className="font-semibold text-green-700">Green</span> →
                On-time coverage is strong for this row's weighting of
                open-order SKUs.
              </p>
              <p>
                <span className="font-semibold text-amber-700">Yellow</span> →
                Partial coverage; some open orders still outstanding for
                near-term weeks.
              </p>
              <p>
                <span className="font-semibold text-rose-700">Red</span> → Low
                or no coverage; most open-order SKUs are not yet on time.
              </p>
            </div>
          )}
        </div>
        <div className="shrink-0 text-right text-[11px] text-muted-foreground">
          <div>Last Updated</div>
          <div>{lastUpdate}</div>
        </div>
      </div>

      {/* Filter grid — 6 columns, shared row 1 + tab-specific rows below */}
      <div className="grid grid-cols-6 gap-4">
        <FilterField label="Snapshot Date">
          <Select value={snapshotDate} onValueChange={setSnapshotDate}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SNAPSHOT_DATE_OPTIONS.map(o => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Department">
          <IdSelect
            value={departmentId}
            onValueChange={handleDepartmentChange}
            options={departmentOptions}
            loading={departmentsLoading}
          />
        </FilterField>
        <FilterField label="Class">
          <IdSelect
            value={classId}
            onValueChange={handleClassChange}
            options={classOptions}
            loading={classesLoading}
            disabled={!departmentId}
          />
        </FilterField>
        <FilterField label="Subclass">
          <IdSelect
            value={subclassId}
            onValueChange={setSubclassId}
            options={subclassOptions}
            loading={subclassesLoading}
            disabled={!classId}
          />
        </FilterField>
        <FilterField label="DC">
          <SimpleSelect
            value={dc}
            onValueChange={setDc}
            options={DC_OPTIONS}
            formatOption={formatDistributionCenterName}
          />
        </FilterField>
        <FilterField label="POG Set Date">
          <div className="flex flex-col gap-1">
            <Input
              type="date"
              className="w-full bg-white"
              value={pogSetDateStart ?? ''}
              onChange={e => setPogSetDateStart(e.target.value || null)}
            />
            <Input
              type="date"
              className="w-full bg-white"
              value={pogSetDateEnd ?? ''}
              onChange={e => setPogSetDateEnd(e.target.value || null)}
            />
          </div>
        </FilterField>

        <FilterField label="Seasonal/Core">
          <SimpleSelect
            value={seasonalCore}
            onValueChange={setSeasonalCore}
            options={SEASONAL_CORE_OPTIONS}
          />
        </FilterField>
        <FilterField label="Season">
          <IdSelect
            value={seasonId}
            onValueChange={setSeasonId}
            options={seasonOptions}
            loading={seasonsLoading}
          />
        </FilterField>
        <FilterField label="Event">
          <SimpleSelect
            value={event}
            onValueChange={setEvent}
            options={EVENT_OPTIONS}
          />
        </FilterField>
        <FilterField label="POG Group">
          <SimpleSelect
            value={pogGroup}
            onValueChange={setPogGroup}
            options={POG_GROUP_OPTIONS}
          />
        </FilterField>
        {isDetailsTab ? (
          <FilterField label="Initial/Replen">
            <SimpleSelect
              value={initialReplen}
              onValueChange={setInitialReplen}
              options={INITIAL_REPLEN_OPTIONS}
            />
          </FilterField>
        ) : (
          <FilterField label="POG Set Type">
            <SimpleSelect
              value={pogSetType}
              onValueChange={setPogSetType}
              options={POG_SET_TYPE_OPTIONS}
            />
          </FilterField>
        )}
        {!isDetailsTab ? (
          <FilterField label="Initial/Replen">
            <SimpleSelect
              value={initialReplen}
              onValueChange={setInitialReplen}
              options={INITIAL_REPLEN_OPTIONS}
            />
          </FilterField>
        ) : (
          // Row 2, column 6 is empty for the Details tab (matches the
          // screenshot) — this placeholder forces the PO/SKU/POG Set
          // Type/Vendor row below to start at column 1 instead of
          // continuing the grid auto-flow into this row's last cell.
          <div />
        )}

        {isDetailsTab && (
          <>
            <FilterField label="PO">
              <SimpleSelect
                value={po}
                onValueChange={setPo}
                options={PO_OPTIONS}
              />
            </FilterField>
            <FilterField label="SKU">
              <SimpleSelect
                value={sku}
                onValueChange={setSku}
                options={SKU_OPTIONS}
              />
            </FilterField>
            <FilterField label="POG Set Type">
              <SimpleSelect
                value={pogSetType}
                onValueChange={setPogSetType}
                options={POG_SET_TYPE_OPTIONS}
              />
            </FilterField>
            <FilterField label="Vendor">
              <SimpleSelect
                value={vendor}
                onValueChange={setVendor}
                options={VENDOR_OPTIONS}
              />
            </FilterField>
          </>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as ActiveTab)}
        className="mt-2"
      >
        <TabsList>
          <TabsTrigger value="pog-on-time-projection">
            POG On-Time Projection
          </TabsTrigger>
          <TabsTrigger value="pog-details">POG Details</TabsTrigger>
        </TabsList>

        <TabsContent value="pog-on-time-projection">
          <POGOnTimeProjectionTable data={projectionData} />
        </TabsContent>

        <TabsContent value="pog-details">
          {detailRows.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center rounded-lg border border-border bg-white text-sm text-muted-foreground">
              No results for the selected filters.
            </div>
          ) : (
            <POGDetailsTable
              rows={detailRows}
              totalOverride={detailsTotalOverride}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
